<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { useIdeStore } from '@/stores/ide.store'
import RegisterTab from '@/components/header/auth/RegisterTab.vue'
import Button from '@/components/atoms/button/index.vue'
import { EXECUTION_LIMIT } from '@/utils/sharedData/plans'
import {
  freePlanBenefits,
  proPlanBenefits,
  premiumPlanBenefits
} from '@/utils/sharedData/planBenefits'
import check from '@/assets/images/ide/check-circle.svg'

const router = useRouter()
const authStore = useAuthStore()
const ideStore = useIdeStore()

const waitingPeriod = ref<string>('')

const planBenefits = computed(() => {
  switch (ideStore.executionLimit) {
    case EXECUTION_LIMIT.PER_DAY10:
      return freePlanBenefits
    case EXECUTION_LIMIT.PER_DAY100:
      return proPlanBenefits
    case EXECUTION_LIMIT.PER_MONTH1000:
      return premiumPlanBenefits
    default:
      return null
  }
})

const showRegisterForm = computed(() => {
  return ideStore.executionLimit === EXECUTION_LIMIT.PER_DAY10
})

watch(
  () => ideStore.executionLimit,
  (value) => {
    if (value === EXECUTION_LIMIT.PER_MONTH1000) {
      waitingPeriod.value = '1 month'
    } else {
      waitingPeriod.value = '24 hours'
    }
  },
  { immediate: true }
)

watch(
  () => authStore.isUserloggedIn,
  (loggedIn) => {
    if (loggedIn) {
      ideStore.resetExecutionLimit()
    }
  }
)

/**
 * Handles the upgrade plan action by navigating to the pricing page
 */
const handleUpgradePlan = () => {
  router.push('/pricing')
}
</script>

<template>
  <div v-if="planBenefits" class="mx-auto mt-10 w-full max-w-xl p-4">
    <h2 class="text-high mb-4 text-lg font-bold">{{ planBenefits.title }}</h2>
    <ul class="text-high space-y-3">
      <li
        v-for="(point, index) in planBenefits.points"
        :key="index"
        class="flex items-center gap-2"
      >
        <img :src="check" alt="check" class="h-4 w-4" />
        <span v-html="point"></span>
      </li>
    </ul>
    <!-- Divider -->
    <hr className="my-5 h-0.5 border-t-0 surface-primary-header" />

    <div v-if="showRegisterForm" class="mb-8">
      <RegisterTab />
    </div>
    <div v-else>
      <Button variant="filled" size="medium" class="w-full" @click="handleUpgradePlan">
        Upgrade Plan
      </Button>
      <p v-if="waitingPeriod" class="text-mid mt-2 text-center">
        Or wait for <strong class="text-high">{{ waitingPeriod }}</strong>
      </p>
    </div>
  </div>
</template>
